import { Container, Grid, Typography, Button, LinearProgress, Alert, AlertTitle, Box } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import BasicUserData from './componets/BasicDataUser';
import { useEffect, useMemo, useState } from 'react';
import { ContactDetails, dictionaryService, FormDataB2B, Token, sanitizeFormData } from './services/dictionaryService';
import { isValidNIP, isValidREGON, phoneRegex, getIbanError } from './helpers/validation';
import ContactDataB2B from './componets/ContactDataB2B';
import CompanyData from './componets/CompanyData';
import AccoumtB2B from './componets/AccountB2B';
import IbanField from './componets/IbanField';
import { IBAN_PLACEHOLDER, getBankNameFromIban } from './helpers/iban';
import RepresentativePerson from './componets/RepresentivePerson';
import AgreementB2B from './componets/AgreementB2B';
import SmsVerificationDialog from './componets/SmsVerificationDialog';
import { TranslateFn, useLanguage } from './i18n/LanguageContext';

export interface FormValuesB2B {
    dataAgreement: boolean;
    personalDataProcessingConsent: boolean;
    firstName: string;
    lastName: string;
    email: string;
    phoneNo: string;
    companyName: string;
    nip: string;
    regon: string;
    legalForm: string;
    representativePersonFirstName: string;
    representativePersonLastName: string;
    representativePersonPosition: string;
    province: string;
    district: string;
    commune: string;
    city: string;
    postalCode: string;
    street: string;
    houseNumber: string;
    apartmentNumber: string | null;
    accountNumber: string;
}

const buildValidationSchema = (t: TranslateFn) => Yup.object({
    dataAgreement: Yup.boolean().oneOf([true], t('v.required')),
    personalDataProcessingConsent: Yup.boolean().oneOf([true], t('v.required')),
    firstName: Yup.string().required(t('v.firstNameRequired')),
    lastName: Yup.string().required(t('v.lastNameRequired')),
    email: Yup.string().email(t('v.emailInvalid')).required(t('v.emailRequired')),
    phoneNo: Yup.string()
        .required(t('v.phoneRequired'))
        .matches(phoneRegex, t('v.phoneInvalid')),
    companyName: Yup.string().required(t('v.companyNameRequired')),
    nip: Yup.string()
        .required(t('v.nipRequired'))
        .test('is-valid-nip', t('v.nipInvalid'), value => !value || isValidNIP(value)),
    regon: Yup.string()
        .required(t('v.regonRequired'))
        .test('is-valid-regon', t('v.regonInvalid'), value => !value || isValidREGON(value)),
    legalForm: Yup.string().required(t('v.legalFormRequired')),
    representativePersonFirstName: Yup.string().required(t('v.repFirstNameRequired')),
    representativePersonLastName: Yup.string().required(t('v.repLastNameRequired')),
    representativePersonPosition: Yup.string().required(t('v.repPositionRequired')),
    province: Yup.string().required(t('v.voivodeshipRequired')),
    commune: Yup.string().required(t('v.municipalityRequired')),
    city: Yup.string().required(t('v.cityRequired')),
    postalCode: Yup.string()
        .required(t('v.postalRequired'))
        .matches(/^\d{2}-\d{3}$/, t('v.postalFormat')),
    street: Yup.string().required(t('v.streetRequired')),
    houseNumber: Yup.string()
        .required(t('v.houseRequired'))
        .max(6, t('v.houseMax')),
    apartmentNumber: Yup.string()
        .nullable()
        .max(6, t('v.aptMax')),
    accountNumber: Yup.string()
        .required(t('iban.required'))
        // createError, żeby pokazać konkretną przyczynę zamiast jednego ogólnego komunikatu
        .test('is-valid-iban', '', function (value) {
            const error = getIbanError(value ?? '');
            return error ? this.createError({ message: t(error.key, error.params) }) : true;
        }),
});

const FormPageB2B = ({ tokenAll, contactDetails }: { tokenAll: Token, contactDetails: ContactDetails }) => {

    const { lang, t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [send, setSend] = useState(false);
    // Dane przygotowane do wysyłki czekają na podpis kodem SMS; zapis następuje dopiero z dowodem.
    const [pendingData, setPendingData] = useState<FormDataB2B | null>(null);

    // Schemat zależy od języka — komunikaty walidacji muszą wyjść w wybranym języku.
    const validationSchema = useMemo(() => buildValidationSchema(t), [t]);

    const formik = useFormik<FormValuesB2B>({
        initialValues: {
            dataAgreement: false,
            personalDataProcessingConsent: false,
            firstName: '',
            lastName: '',
            email: '',
            phoneNo: '',
            companyName: "",
            nip: "",
            regon: "",
            legalForm: '',
            representativePersonFirstName: "",
            representativePersonLastName: "",
            representativePersonPosition: "",
            province: '',
            district: '',
            commune: '',
            city: '',
            postalCode: '',
            street: '',
            houseNumber: '',
            apartmentNumber: '',
            accountNumber: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            if (!tokenAll) return;
            const preparedData: FormDataB2B = {
                formType: "B2B" as const,
                status: "SUBMITTED",
                language: lang,
                consents: {
                    dataAgreement: values.dataAgreement,
                    personalDataProcessingConsent: values.personalDataProcessingConsent,
                },
                contactDetails: {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                    phoneNo: values.phoneNo,
                },
                token: tokenAll,
                name: values.companyName,
                taxId: values.nip,
                statisticalId: values.regon,
                legalForm: values.legalForm,
                registeredOfficeAddress: {
                    country: "Polska",
                    voivodeship: values.province,
                    county: values.district,
                    municipality: values.commune,
                    street: values.street,
                    houseNumber: values.houseNumber,
                    apartmentNumber: values.apartmentNumber || undefined,
                    postalCode: values.postalCode,
                    city: values.city,
                },
                representativePerson: {
                    firstName: values.representativePersonFirstName,
                    lastName: values.representativePersonLastName,
                    position: values.representativePersonPosition,
                },
                settlementData: {
                    accountNumber: values.accountNumber,
                    bankName: getBankNameFromIban(values.accountNumber) ?? '',
                },
                declarationFooter: {
                    date: new Date().toISOString(),
                },
            };
            // Otwiera dialog SMS — właściwy zapis wykonuje handleSigned po weryfikacji kodu.
            setPendingData(sanitizeFormData(preparedData));
        },

    });

    const handleSigned = async (proof: string) => {
        if (!tokenAll || !pendingData) return;
        setLoading(true);
        try {
            await dictionaryService.postFormB2B(tokenAll.value, pendingData, proof);
            setPendingData(null);
            setSend(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        formik.setValues((prevValues) => ({
            ...prevValues,
            firstName: contactDetails.firstName,
            lastName: contactDetails.lastName,
            email: contactDetails.email,
            phoneNo: contactDetails.phoneNo,
            companyName: contactDetails.companyName || prevValues.companyName,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenAll, contactDetails])

    // Po zmianie języka już wyświetlone komunikaty walidacji mają wyjść w nowym języku.
    useEffect(() => {
        if (formik.submitCount > 0) formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang]);

    return (
        <>
            <Container maxWidth="lg">
                {send ? (
                    <Alert severity="success" sx={{ marginTop: '32px' }}>
                        <AlertTitle>{t('form.sent.title')}</AlertTitle>
                        {t('form.sent.body')}
                    </Alert>
                ) : (
                    <form onSubmit={formik.handleSubmit}>
                        <Box sx={{
                            mt: 10, mb: 4,
                            borderRadius: '8px',
                            padding: '24px',
                            fontSize: '24px',
                            fontFamily: '"Poppins", sans-serif', // Set font family to Poppins
                            backgroundColor: "#E0F2F1",
                            border: '1px solid #00796B',
                            marginTop: { xs: "24px", md: "32px" },
                            paddingLeft:
                                { xs: "15px", lg: "24px" },
                            paddingRight: "12px"
                        }}>
                            <Typography variant="h5" gutterBottom>
                                {t('form.intro.hello')}
                            </Typography>
                            <Typography variant="body1" gutterBottom >
                                {t('form.intro.line1')}
                                <br />
                                <br />
                                {t('form.intro.line2a')}<span style={{ fontWeight: "600" }}>{t('form.intro.line2bold')}</span>{t('form.intro.line2b')}
                                <br />
                            </Typography>
                        </Box>
                        <BasicUserData<FormValuesB2B> formik={formik} />
                        {/* <BillingDetails formik={formik} /> */}

                        <CompanyData formik={formik} />
                        <RepresentativePerson formik={formik} />

                        <ContactDataB2B formik={formik} />


                        <AccoumtB2B
                            formik={formik}
                            title={t('section.bankAccount')}
                            name="accountNumber"
                            input={
                                (
                                    <IbanField
                                        sx={{ width: '100%' }}
                                        inlineLabels
                                        value={formik.values.accountNumber}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={IBAN_PLACEHOLDER}
                                        setValue={(v) => formik.setFieldValue('accountNumber', v)}
                                        error={formik.touched.accountNumber && Boolean(formik.errors.accountNumber)}
                                        helperText={formik.touched.accountNumber && formik.errors.accountNumber}
                                    />
                                )
                            }
                        />

                        <Typography variant="h6" sx={{ marginTop: { xs: '80px', md: '130px' }, paddingBottom: { sx: '5px', md: '20px' }, marginBottom: { xs: '10px', md: '15px' }}}>
                            {t('section.contractorStatement')}
                        </Typography>
                        <AgreementB2B formik={formik} />
                        {Object.keys(formik.errors).length > 0 && formik.submitCount > 0 && (
                            <Alert severity="error" sx={{ marginTop: 2 }}>
                                {t('form.errorsSummary')}
                            </Alert>
                        )}
                        <Grid container justifyContent="center" sx={{ marginTop: 10, marginBottom: 10, paddingBottom: '200px' }}>
                            {loading ? (
                                <LinearProgress sx={{ width: '100%' }} />
                            ) : (
                                <Button variant="contained" type="submit" color="primary" sx={{ backgroundColor: '#00796B', '&:hover': { backgroundColor: '#005A4F' } }}>
                                    {t('form.submit')}
                                </Button>
                            )}
                        </Grid>
                    </form>
                )}
            </Container>

            <SmsVerificationDialog
                open={pendingData !== null}
                token={tokenAll.value}
                onSigned={handleSigned}
                onClose={() => setPendingData(null)}
            />
        </>
    );
};

export default FormPageB2B;
